import { useQuery } from "@tanstack/react-query";
import { useDataSource } from "@/data/DataProvider";
import { useIdentity } from "@/auth/ClientAuthProvider";
import type { TeamMember } from "@/data/types";
import { displayName } from "@/lib/names";

export type { TeamMember };

/** The people helping this client: strategist first, then coordinator. */
export function useTeam() {
  const source = useDataSource();
  const id = useIdentity();
  return useQuery({
    queryKey: ["team", id.orgId, id.leadId],
    queryFn: async () => {
      const team = await source.getTeam(id);
      const order: TeamMember["role"][] = ["strategist", "coordinator"];
      return [...team].sort((a, b) => order.indexOf(a.role) - order.indexOf(b.role));
    },
    staleTime: 30 * 60_000,
  });
}

/** The strategist alone, for screens that only need them. */
export function useStrategist() {
  const team = useTeam();
  return { ...team, data: team.data?.find((m) => m.role === "strategist") ?? null };
}

export function strategistDisplayName(s: TeamMember | null | undefined): string {
  return displayName(s?.name) || "Your strategist";
}
